import { Icon, Button } from '@scale/scaleui';
import { Attachment, ModelInputFileAttachmentType } from '@scale/shared/types/chat';
import { useContext, useState, useEffect } from 'react';
import EditAttachmentsModal from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/EditAttachmentsModal';
import AttachmentChip from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/AttachmentChip';
import {
  FILE_ATTACHMENTS_MAX_ATTACHMENTS,
  FILE_ATTACHMENTS_ALLOWED_MIME_TYPES_BY_INPUT_TYPE,
} from '../constants';
import ChatAttachmentCarousel from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/ChatAttachmentCarousel';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { MultimodalImageServiceHook } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useMultimodalImageService';
import { MultimodalAssetDto } from '@scale/mm-shared';
import MultimodalPromptSuggestionsCard from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/MultimodalPromptSuggestionsCard';
import _ from 'lodash';
import { Event, logEvent } from 'react-scale/lib/events';
import { MultimodalPromptSuggestionEventType } from '@scale/shared/types/events/MultimodalPromptSuggestionEvent';

type AttachmentGroupProps = {
  attachments: Attachment[];
  setAttachments: (attachments: Attachment[]) => void;
  readOnly?: boolean;
  enableAttachments?: boolean;
  enableImageUrls?: boolean;
  enableMmImageService?: boolean;
  multimodalImageServiceHook: MultimodalImageServiceHook;
  appendPromptText?: (textToAppend: string) => void;
};

const AttachmentGroup = ({
  attachments,
  setAttachments,
  readOnly,
  enableAttachments,
  enableImageUrls,
  enableMmImageService,
  multimodalImageServiceHook,
  appendPromptText,
}: AttachmentGroupProps) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [carouselIndex, setCarouselIndex] = useState<number>(0);
  const config = useChatStoreExtract(state => state.config);
  const { projectId, assignmentId } = useContext(ChatTaskContext);
  // record mapping image urls to multimodal image db assets
  const [imagesToMetadata, setImagesToMetadata] = useState<Record<string, MultimodalAssetDto>>({});

  const maxAttachments = config.maxAttachments ?? FILE_ATTACHMENTS_MAX_ATTACHMENTS;
  const supportedMimeTypesByInputType = FILE_ATTACHMENTS_ALLOWED_MIME_TYPES_BY_INPUT_TYPE;

  // Generally we want to store the original url directly in the response without any mirroring but for some customers we need to mirror the image to a temp Scale-hosted bucket for delivery reliability
  const shouldMirrorImageUrlsToTempBucket = useUnleashFeatureFlag(
    'should-mirror-multimodal-images',
    { projectId },
    false,
  );

  const promptSuggestionsEnabled = useUnleashFeatureFlag(
    'multimodal-enable-prompt-suggestions',
    { projectId },
    false,
  );

  const deleteAttachment = (index: number) => {
    const attachment = attachments[index];
    if (attachment.url) {
      setImagesToMetadata(imagesToMetadata => _.omit(imagesToMetadata, attachment.url!));
    }
    if (attachment.originalUrl) {
      multimodalImageServiceHook.releaseImageClaim(attachment.originalUrl);
    }
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
    if (carouselIndex >= index && carouselIndex > 0) {
      setCarouselIndex(Math.max(carouselIndex - 1, newAttachments.length - 1));
    }
  };

  useEffect(() => {
    multimodalImageServiceHook.setPromptSuggestions(undefined);
  }, [attachments]);

  // Avoid rendering a blank mt-2 div if there's nothing to render
  if (!(attachments && attachments.length > 0) && !(attachments.length < maxAttachments)) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 mt-2">
      <div className="flex flex-row gap-2 items-center justify-between">
        {attachments.length < maxAttachments && (
          <>
            {!readOnly && (
              <Icon
                hasBackground
                size="sm"
                icon="paperclip"
                variant="primary"
                onClick={() => {
                  setShowModal(true);
                }}
                data-testid="add-attachment-button"
              />
            )}
            <EditAttachmentsModal
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              attachments={attachments}
              setAttachments={setAttachments}
              supportedMimeTypesByInputType={supportedMimeTypesByInputType}
              shouldMirrorImageUrlsToTempBucket={shouldMirrorImageUrlsToTempBucket}
              enableAttachments={enableAttachments}
              enableImageUrls={enableImageUrls}
              enableMmImageService={enableMmImageService}
              multimodalImageServiceHook={multimodalImageServiceHook}
              updateImageMetadata={(url: string, metadata: MultimodalAssetDto) => {
                setImagesToMetadata({ ...imagesToMetadata, [url]: metadata });
              }}
            />
          </>
        )}
        {!readOnly &&
          attachments.length === 1 &&
          attachments[0].fileType === ModelInputFileAttachmentType.Image &&
          attachments[0].url &&
          promptSuggestionsEnabled && (
            // Only show prompt suggestion button if there is a single image attachment with a url and if not in read-only mode
            // Currently also feature flagged
            <Button
              size="sm"
              variant="primary"
              onClick={async () => {
                await logEvent(Event.MultimodalPromptSuggestionEvent, {
                  type: MultimodalPromptSuggestionEventType.PROMPT_SUGGESTION_GENERATION,
                  projectId,
                  assignmentId,
                  imageUrl: attachments[0].url!,
                });
                await multimodalImageServiceHook.fetchPromptSuggestions(attachments[0].url!);
              }}
              disabled={multimodalImageServiceHook.isFetchingPromptSuggestions}
            >
              {multimodalImageServiceHook.promptSuggestions ? 'Regenerate' : 'Generate'} Prompt
              Suggestions
            </Button>
          )}
      </div>
      {(multimodalImageServiceHook.promptSuggestions ||
        multimodalImageServiceHook.isFetchingPromptSuggestions) &&
        attachments[0]?.url && (
          <MultimodalPromptSuggestionsCard
            imageUrl={attachments[0].url}
            isFetchingPromptSuggestions={multimodalImageServiceHook.isFetchingPromptSuggestions}
            promptSuggestions={multimodalImageServiceHook.promptSuggestions}
            appendPromptText={appendPromptText}
          />
        )}
      {attachments && attachments.length > 0 && (
        <div className="flex flex-col gap-2">
          <div>
            {attachments.map((attachment: Attachment, index: number) => (
              <AttachmentChip
                key={index}
                attachment={attachment}
                deleteAttachment={() => deleteAttachment(index)}
                readOnly={readOnly}
              />
            ))}
          </div>
          {attachments.some(
            attachment =>
              attachment.fileType === ModelInputFileAttachmentType.Image ||
              attachment.fileType === ModelInputFileAttachmentType.Audio,
          ) && (
            <ChatAttachmentCarousel
              index={carouselIndex}
              setIndex={setCarouselIndex}
              attachments={attachments}
              deleteAttachment={deleteAttachment}
              readOnly={!!readOnly}
              imagesToMetadata={imagesToMetadata}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default AttachmentGroup;
